'use client';

import { Calendar, MessageSquareText, PencilLineIcon, Trash2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useParams, useRouter } from 'next/navigation';
import React, { useState } from 'react';

import { Button } from '@ui/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ui/components/ui/tooltip';
import { isFeatureEnabled } from '@website/config/featureFlags';
import { useCurrentUser } from '@website/hooks/useCurrentUser';

import { Activity } from '../../../../../services/activities.service';
import { DeleteActivityDialog } from './DeleteActivityDialog';
import { EditActivityModal } from './EditActivityModal';

interface ActivityOwnerActionsProps {
  activity: Activity;
}

export function ActivityOwnerActions({ activity }: ActivityOwnerActionsProps) {
  const { data: user } = useCurrentUser();
  const isOwner = !!user && user.isProfessional && !!activity.professionalId && user.id === activity.professionalId;
  const router = useRouter();
  const { locale } = useParams();
  const t = useTranslations('ActivityDetail');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  if (!isOwner) return null;

  // Get the earliest active slot date for calendar navigation
  const getEarliestSlotDate = () => {
    if (!activity.slots || activity.slots.length === 0) {
      return null;
    }

    // Filter active slots (exclude DELETED, CANCELLED, COMPLETED)
    const activeSlots = activity.slots.filter(
      (slot) => slot.status !== 'DELETED' && slot.status !== 'CANCELLED' && slot.status !== 'COMPLETED'
    );

    if (activeSlots.length === 0) {
      return null;
    }

    // Sort by startDate and get the earliest
    const sortedSlots = [...activeSlots].sort(
      (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    );

    return sortedSlots[0].startDate;
  };

  const handleCalendarClick = () => {
    const slotDate = getEarliestSlotDate();
    const calendarUrl = slotDate
      ? `/${locale}/agenda?date=${slotDate}&activityId=${activity.id}`
      : `/${locale}/agenda?activityId=${activity.id}`;
    router.push(calendarUrl);
  };

  return (
    <>
      <TooltipProvider delayDuration={0}>
        <div className="hidden md:flex items-center gap-3">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" onClick={handleCalendarClick} aria-label={t('viewSchedule')}>
                <Calendar className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('viewSchedule')}</TooltipContent>
          </Tooltip>

          {isFeatureEnabled('RATING') && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  onClick={() => router.push(`/${locale}/activity/${activity.id}/reviews`)}
                  aria-label={t('viewReviews')}
                >
                  <MessageSquareText className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('viewReviews')}</TooltipContent>
            </Tooltip>
          )}

          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" onClick={() => setIsEditModalOpen(true)} aria-label={t('editActivity')}>
                <PencilLineIcon className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('editActivity')}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                onClick={() => setIsDeleteOpen(true)}
                aria-label={t('deleteActivity')}
                className="bg-error-100 text-error-700 hover:bg-error-200"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('deleteActivity')}</TooltipContent>
          </Tooltip>

          <DeleteActivityDialog
            activityId={activity.id}
            activityTitle={activity.title}
            isOpen={isDeleteOpen}
            onOpenChange={setIsDeleteOpen}
          />
        </div>
      </TooltipProvider>

      <EditActivityModal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} activityId={activity.id} />
    </>
  );
}
